import { ACCOUNT_ALREADY_EXISTS_ERROR_CODE } from '@commutifi/constants/Errors'
import {
  ManagerAccount,
  ManagerCreationPayload,
  ManagerEnterprise,
  usePostManagerAccountEnterprise
} from 'api/modules/enterprises'
import { useEnterpriseRegistration } from 'api/modules/enterprisesRegistrations'
import { spacing } from 'constants/global'
import { FormattedMessage, IntlShape, useIntl } from 'locales/index'
import pick from 'lodash/pick'
import React, { FocusEventHandler } from 'react'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import CommutifiTermsCheckbox from 'shared/components/common/CommutifiTermsCheckbox'
import SuggestOfficeSelect, { validateOffice } from 'shared/components/common/SuggestOfficeSelect'
import { EmailInput, PasswordInput } from 'shared/components/common/forms'
import { PasswordField } from 'shared/components/common/forms/Password'
import HeadlessLayout from 'shared/components/layout/HeadlessLayout'
import {
  Alert,
  Input as AppInput,
  Button,
  Card,
  Col,
  Form,
  GenericError,
  Image,
  Input,
  MarkdownWrapper,
  PhoneInputValue,
  Row,
  SectionTitle,
  Skeleton,
  Spin,
  Typography
} from '@commutifi-fe/ui'
import { useNotifications } from '@commutifi-fe/commutifi-specific/hooks'
import { HttpStatusCode } from '@commutifi-fe/constants'
import { OfficeGeocodeValue } from 'shared/interfaces'
import { EnterpriseRegistrationPages, PublicPathsParams, PublicRouting } from 'src/routing'
import DDOTLogo from 'static/logo/ddot-logo.svg'
import { Overwrite } from 'utility-types'
import { CountryCode, parsePhoneNumberToInternational } from 'utils/phoneNumber'
import styles from './styles.module.scss'
import RegistrationSuccess from './views/RegistrationSuccess'
import { useEnterpriseEin } from 'api/modules/compliance'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLevelUpAlt } from '@commutifi-fe/custom-icons/solid'
import { PhoneNumberWithExtFormItems } from '@commutifi-fe/commutifi-specific/Presentation/forms'

type EnterpriseRegistrationForm = Overwrite<
  ManagerCreationPayload,
  {
    account: Overwrite<ManagerAccount, { phoneNumber: PhoneInputValue & { ext: string } }>
    enterprise: Overwrite<ManagerEnterprise, { address: OfficeGeocodeValue }>
  }
>

const contentByRegistrationId = (
  intl: IntlShape
): Record<
  string,
  {
    title: string
    content: string
    logoSrc?: string
    createAccountSubtitle?: string
    buildingAddressQuestionLabel: string
    expiryDate?: string
    expiryMessage?: string
    expiryMessageDescription?: string
  }
> => ({
  dccashout: {
    title: intl.formatMessage({ id: 'organizations.registration.ddot.title' }),
    content:
      'The DC Parking Cashout Law, also known as the Transportation Benefits Equity Amendment Act of 2020, mandates employers with 20 or more covered employees who offer free or subsidized parking benefits to employees to implement one of three compliance options that encourage commuting using sustainable transportation options instead of driving to work.\n\nAll District employers with 20+ covered employees must report their compliance or exemption to DDOT biennially.\n\n**Next reporting due date:**\n\n**- January 15, 2025 for all employers complying via TDM Plan, Clean Air Compliance Fee, or expiring exemption**\n\n**- January 15, 2025 for others**',
    expiryMessage: intl.formatMessage({ id: 'organizations.registration.ddot.expiry.message' }),
    logoSrc: DDOTLogo,
    createAccountSubtitle: intl.formatMessage({ id: 'organizations.registration.ddot.tip' }),
    buildingAddressQuestionLabel: intl.formatMessage({ id: 'enterprises.labels.address.DC' }),
    expiryDate: '2023-03-01T00:00:00.000Z'
  }
})

function EnterpriseRegistration({
  match,
  history
}: RouteComponentProps<{ [PublicPathsParams.RegistrationId]: string }>) {
  const intl = useIntl()
  const commutifiNotification = useNotifications()

  const parkingCashoutEmail = 'parkingcashout@dc.gov'

  const [einEnterprise, setEinEnterprise] = React.useState<string>('')

  const handleSearch: React.KeyboardEventHandler<HTMLInputElement> & FocusEventHandler<HTMLInputElement> = (e) => {
    setEinEnterprise(e.currentTarget.value)
  }
  const { error, isSuccess, refetch } = useEnterpriseEin(einEnterprise)

  React.useEffect(() => {
    refetch()
  }, [einEnterprise, refetch])

  const handlePageReload = () => {
    window.location.reload()
  }

  const {
    isLoading,
    data,
    error: fetchEnterpriseRegistrationError
  } = useEnterpriseRegistration(match.params.enterpriseRegistrationId)

  const { isPending: isCreating, mutateAsync: createManager } = usePostManagerAccountEnterprise(
    match.params.enterpriseRegistrationId
  )
  const [errorKey, setErrorKey] = React.useState<'error.duplicate' | null>(null)
  const pageContent = React.useMemo(
    () => contentByRegistrationId(intl)[match.params.enterpriseRegistrationId],
    [intl, match.params.enterpriseRegistrationId]
  )

  const handleCreateEnterprise = async (managerInfo: EnterpriseRegistrationForm) => {
    const address = pick(managerInfo.enterprise.address, ['formattedAddress', 'location'])
    const formattedPayload: ManagerCreationPayload = {
      enterprise: {
        ...managerInfo.enterprise,
        address: !address.formattedAddress ? undefined : address
      },
      account: {
        ...managerInfo.account,
        phoneNumber: managerInfo.account.phoneNumber?.phone
          ? parsePhoneNumberToInternational(
              managerInfo.account.phoneNumber.phone,
              managerInfo.account.phoneNumber.short as CountryCode | undefined
            )
          : undefined
      }
    }

    try {
      const createdAccount = await createManager(formattedPayload)
      setErrorKey(null)
      history.push(EnterpriseRegistrationPages.Success, { id: createdAccount?.id, email: createdAccount?.email })
    } catch (e) {
      const errorCode = e.data?.code
      if (errorCode === ACCOUNT_ALREADY_EXISTS_ERROR_CODE) {
        setErrorKey('error.duplicate')
      } else {
        commutifiNotification.error({ description: intl.formatMessage({ id: 'error.500.text' }) })
      }
    }
  }

  const handleCloseError = () => setErrorKey(null)
  const handleGoToLogin = () => history.push(PublicRouting.auth.path)

  return fetchEnterpriseRegistrationError ? (
    <GenericError
      type="page"
      actions={
        <Button type="primary" onClick={handlePageReload}>
          <FormattedMessage id="global.page.reload" />
        </Button>
      }
    />
  ) : (
    <HeadlessLayout>
      <Row style={{ maxWidth: '100%' }}>
        <Col xl={12} className="u-margin-bottom--large-4" style={{ paddingRight: '120px' }}>
          <Typography.Title loading={isLoading} styleguide="marketing">
            {pageContent.title}
          </Typography.Title>
          {isLoading ? (
            <Skeleton
              active
              title={false}
              paragraph={{
                rows: 2,
                width: ['95%', '90%']
              }}
            />
          ) : (
            <Typography.Text
              styleguide="marketing"
              type="secondary"
              style={{ display: 'block', padding: `${spacing.large} 0` }}
            >
              <MarkdownWrapper source={pageContent.content} />
            </Typography.Text>
          )}
          {pageContent.logoSrc ? (
            <Image src={pageContent.logoSrc} alt="Compliance organization logo" className="u-margin-top--medium" />
          ) : null}
        </Col>
        <Col xl={12}>
          <Card className={styles['register-card']} styles={{ body: { padding: spacing.large4 } }}>
            <SectionTitle
              level={2}
              title="Already have an account?"
              subtitle="Log in to your account, you can reset the password if needed."
            />
            <Button type="primary" onClick={handleGoToLogin} block>
              Log in
            </Button>

            <SectionTitle
              className="u-margin-top--large-2"
              level={2}
              variant="h4"
              title="Search for your company"
              subtitle="Enter your company EIN/DC Business number to find an existing account"
            />
            <Input.Search
              placeholder="Search by EIN/DC Business number"
              prefix={false}
              suffix={
                <FontAwesomeIcon
                  icon={faLevelUpAlt}
                  flip="vertical"
                  transform={{ rotate: -90 }}
                  className="u-margin-top--tiny-2"
                />
              }
              className="u-margin-bottom--medium"
              onPressEnter={handleSearch}
              onBlur={handleSearch}
            />
            {error?.status === HttpStatusCode.NotFound && (
              <Alert type="info" message="We couldn't find your company, please create an account below." />
            )}
            {einEnterprise.length > 0 && isSuccess ? (
              <Alert
                type="success"
                message={
                  <>
                    <Typography.Paragraph>Your company is already in the system</Typography.Paragraph>
                    <Typography.Text>
                      If you know the email that was used for this account, please go to Log In and reset the password.
                      Otherwise, please contact
                    </Typography.Text>{' '}
                    <Typography.Link target="__blank" href={`mailto:${parkingCashoutEmail}`}>
                      {parkingCashoutEmail}
                    </Typography.Link>
                  </>
                }
              />
            ) : null}
            {pageContent.createAccountSubtitle ? (
              <SectionTitle
                className="u-margin-top--large-2"
                level={2}
                title={<FormattedMessage id="organizations.registration.form.title" />}
                subtitle={pageContent.createAccountSubtitle}
              />
            ) : (
              <Typography.Title level={2}>
                <FormattedMessage id="organizations.registration.form.title" />
              </Typography.Title>
            )}
            <Spin size="large" spinning={isLoading}>
              <Form<EnterpriseRegistrationForm>
                layout="vertical"
                requiredMark={false}
                onFinish={handleCreateEnterprise}
                className="u-padding-top--small"
              >
                {errorKey ? (
                  <Alert
                    className="u-margin-bottom--medium-2"
                    type="error"
                    closable
                    onClose={handleCloseError}
                    message={<FormattedMessage id={errorKey} />}
                  />
                ) : null}
                <Form.Item
                  name={['enterprise', 'name']}
                  rules={[{ required: true, message: <FormattedMessage id="required" /> }]}
                  label={intl.formatMessage({
                    id: 'organizations.registration.form.items.companyName'
                  })}
                >
                  <AppInput
                    placeholder={intl.formatMessage({
                      id: 'organizations.registration.form.items.companyName.placeholder'
                    })}
                  />
                </Form.Item>
                <Form.Item
                  name={['account', 'name']}
                  label={intl.formatMessage({
                    id: 'global.name'
                  })}
                  rules={[{ required: true, message: <FormattedMessage id="required" /> }]}
                >
                  <AppInput
                    placeholder={intl.formatMessage({
                      id: 'global.name.input.placeholder'
                    })}
                  />
                </Form.Item>
                <PhoneNumberWithExtFormItems initialValue={{ code: 1 }} name={['account', 'phoneNumber']} />
                <EmailInput label={intl.formatMessage({ id: 'global.email' })} name={['account', 'email']}>
                  <AppInput
                    placeholder={intl.formatMessage({
                      id: 'global.email.input.placeholder'
                    })}
                  />
                </EmailInput>
                <PasswordInput name={['account', 'password']} style={{ marginBottom: spacing.large3 }}>
                  <PasswordField />
                </PasswordInput>
                {data && !data.building?.id ? (
                  <Form.Item
                    label={pageContent.buildingAddressQuestionLabel}
                    name={['enterprise', 'address']}
                    rules={[
                      {
                        validator: (rule: any, options: string | OfficeGeocodeValue) => validateOffice(rule, options),
                        validateTrigger: 'onSelect'
                      }
                    ]}
                  >
                    <SuggestOfficeSelect
                      loading={isLoading}
                      enterpriseId={undefined}
                      allowCreation
                      placeholder={intl.formatMessage({
                        id: 'organizations.registration.form.items.officeAddress.placeholder'
                      })}
                    />
                  </Form.Item>
                ) : null}
                <CommutifiTermsCheckbox />
                <Button
                  size="middle"
                  type="primary"
                  block
                  htmlType="submit"
                  loading={isCreating}
                  className="u-margin-top--small-3"
                >
                  <span>{intl.formatMessage({ id: 'components.registerScreen.form.button.submit' })}</span>
                </Button>
              </Form>
            </Spin>
          </Card>
        </Col>
      </Row>
    </HeadlessLayout>
  )
}

function EnterpriseRegistrationRoutes({ match }: RouteComponentProps) {
  return (
    <Switch>
      <Route exact path={match.path} component={EnterpriseRegistration} />
      <Route exact path={`${match.url}/success`} component={RegistrationSuccess} />
    </Switch>
  )
}

export default EnterpriseRegistrationRoutes
